
import { MEDIA_ASSET_BASE_URL, MEDIA_ASSET_ORDERS_PATH, MEDIA_ASSET_PRODUCT_IMG } from '@/config/constants';

export function findMatchingVariant({
    variants = [],
    selectedColor = '',
    selectedSize = '',
    checkAvailability = true
}) {
    if (!variants || variants.length === 0) {
        return null;
    }

    // MDS / duplicate placeholders: no selection yet must not resolve to the first SKU
    if (!selectedColor && !selectedSize) {
        return null;
    }

    return variants.find(variant => {
        const variantColor = variant.colorName || variant.ColorName;

        // For size matching, check all possible size fields
        const agilitySize = variant.AgilitySize || variant.agilitySize || '';
        const sizeName = variant.sizeName || variant.SizeName || '';

        const colorMatch = !selectedColor || variantColor === selectedColor;

        // Check if selectedSize matches EITHER agilitySize OR sizeName (case-insensitive)
        // This handles products where Browse Catalog shows sizeName but variant has different agilitySize
        let sizeMatch = !selectedSize;
        if (selectedSize && !sizeMatch) {
            const selectedSizeLower = selectedSize.toLowerCase();
            const agilitySizeLower = agilitySize.toLowerCase();
            const sizeNameLower = sizeName.toLowerCase();

            // Match if selectedSize matches agilitySize OR sizeName
            sizeMatch = agilitySizeLower === selectedSizeLower || sizeNameLower === selectedSizeLower;
        }

        const isAvailable = !checkAvailability || 
            variant.availableAtBranch || 
            variant.AvailableAtBranch || 
            variant.isAvailableAtBranch;

        return colorMatch && sizeMatch && isAvailable;
    });
}

/**
 * Preserve image data from original product when getProductsByIds doesn't return it.
 * Search results extract imageUrl from mediaAssets, but getProductsByIds may not include it.
 * @param {Object} fullProduct - The product data returned from getProductsByIds
 * @param {Object} originalProduct - The original product data (from search results)
 * @returns {Object} fullProduct with preserved image data
 */
export function preserveImageData(fullProduct, originalProduct) {
    if (!fullProduct.imageUrl && !fullProduct.ImageUrl) {
        if (originalProduct.imageUrl) {
            fullProduct.imageUrl = originalProduct.imageUrl;
        } else if (originalProduct.mediaAssets) {
            const primaryImage = originalProduct.mediaAssets.find(a => a.assetType === 'ProductPrimaryImage');
            if (primaryImage?.assetUri) {
                fullProduct.imageUrl = `${MEDIA_ASSET_BASE_URL}/${primaryImage.assetUri}`;
            }
        }
    }
    if (!fullProduct.mediaAssets && originalProduct.mediaAssets) {
        fullProduct.mediaAssets = originalProduct.mediaAssets;
    }
    return fullProduct;
}

/**
 * Resolve the image URL for an estimate list item using catalog products and saved options.
 * @param {Object} item - The order item from estimate TieredCosts
 * @param {Array} products - Catalog products for the estimate
 * @returns {string} The full image URL
 */
export function getEstimateListItemImage(item, products = []) {
    let image = item?.ImageUrl || item?.imageUrl || '';
    if (item?.Type !== 'Custom') {
        const productId = item?.ProductId ?? item?.ProductID;
        const product = products.find(p => String(p.productId ?? p.ProductId) === String(productId));
        let options;
        try {
            options = typeof item?.Variant === 'string' ? JSON.parse(item.Variant) : item?.Variant;
        } catch {
            options = null;
        }
        if (product && options && typeof options === 'object') {
            const variant = matchLegacyVariantFromMdsSavedOptions(product.variants || product.Variants || [], options);
            image = variant?.mediaAssets?.[0]?.assetUri || variant?.MediaAssets?.[0]?.AssetUri ||
                variant?.ImageUrl || variant?.imageUrl || image;
        }
    }

    if (typeof image !== 'string' || !image.trim()) {
        return `${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_ORDERS_PATH}noProduct.svg`;
    }
    image = image.trim();
    if (/^https?:\/\//i.test(image)) return image;
    if (/^\/?image\/upload\//.test(image)) {
        return `${MEDIA_ASSET_BASE_URL}/${image.replace(/^\/+/, '')}`;
    }
    return `${MEDIA_ASSET_BASE_URL}${MEDIA_ASSET_PRODUCT_IMG}${image}.jpg`;
}

// ---------------------------------------------------------------------------
// Legacy COLOR parser (MDS ON load only)
// Legacy ColorName = "Size Color". Split using catalog color names (Steve algo).
// Do NOT call when flag OFF or when SIZE is already present on the saved line.
// ---------------------------------------------------------------------------

/** Per-product sorted color lists — reuse across many lines of the same product. */
const legacyColorMatchListCache = new Map();

/**
 * Collect unique catalog color names from variants (single pass).
 * @param {Array} variants
 * @returns {string[]}
 */
export function collectCatalogColorNames(variants = []) {
    if (!variants?.length) return [];
    const names = [];
    const seen = new Set();
    for (let i = 0; i < variants.length; i++) {
        const c = variants[i]?.colorName || variants[i]?.ColorName;
        if (!c) continue;
        const key = String(c);
        if (seen.has(key)) continue;
        seen.add(key);
        names.push(key);
    }
    return names;
}

/**
 * Steve sort: colors with spaces first (longest→shortest), then single-word (longest→shortest).
 * Build once per product; pass { preSorted: true } into parseLegacyConcatenatedColor.
 * @param {string[]} colorNames
 * @returns {string[]}
 */
export function buildLegacyColorMatchList(colorNames = []) {
    if (!colorNames?.length) return [];

    const withSpaces = [];
    const withoutSpaces = [];
    const seen = new Set();

    for (let i = 0; i < colorNames.length; i++) {
        const raw = colorNames[i];
        if (raw == null) continue;
        const c = String(raw).trim();
        if (!c || seen.has(c)) continue;
        seen.add(c);
        if (c.includes(' ')) withSpaces.push(c);
        else withoutSpaces.push(c);
    }

    const byLenDesc = (a, b) => b.length - a.length;
    withSpaces.sort(byLenDesc);
    withoutSpaces.sort(byLenDesc);
    return withSpaces.length ? withSpaces.concat(withoutSpaces) : withoutSpaces;
}

/**
 * Sorted color match list for a product, cached by productId.
 * @param {string|number|null} productId
 * @param {Array} variants
 * @returns {string[]}
 */
export function getSortedLegacyColorMatchList(productId, variants = []) {
    const cacheKey = productId != null && productId !== '' ? String(productId) : null;
    if (cacheKey && legacyColorMatchListCache.has(cacheKey)) {
        return legacyColorMatchListCache.get(cacheKey);
    }
    const list = buildLegacyColorMatchList(collectCatalogColorNames(variants));
    if (cacheKey) {
        legacyColorMatchListCache.set(cacheKey, list);
    }
    return list;
}

/** Clear product color-list cache (e.g. after catalog refresh). */
export function clearLegacyColorMatchListCache() {
    legacyColorMatchListCache.clear();
}

/**
 * Parse legacy concatenated ColorName ("Size Color") into pure color + size.
 * MDS ON load only. Uses suffix match (color at end) for correctness + speed.
 *
 * @param {string} combinedColor - saved COLOR / SelectedColor
 * @param {string[]} colorNames - catalog colors, or pre-sorted list from buildLegacyColorMatchList
 * @param {{ preSorted?: boolean }} [options]
 * @returns {{ color: string, size: string } | null}
 */
export function parseLegacyConcatenatedColor(combinedColor, colorNames = [], options = {}) {
    const combined = String(combinedColor || '').trim();
    if (!combined || !colorNames?.length) return null;

    const ordered = options.preSorted ? colorNames : buildLegacyColorMatchList(colorNames);
    if (!ordered.length) return null;

    const combinedLower = combined.toLowerCase();

    for (let i = 0; i < ordered.length; i++) {
        const color = ordered[i];
        const colorLower = color.toLowerCase();

        // Exact color-only (no size in string)
        if (combinedLower === colorLower) {
            return { color, size: '' };
        }

        // Suffix: "... {color}" — O(1) endsWith per candidate (list already longest-first)
        const suffix = ` ${colorLower}`;
        if (combinedLower.endsWith(suffix)) {
            const size = combined.slice(0, combined.length - color.length).trim();
            // slice leaves trailing space from separator — trim handles it
            if (!size) {
                return { color, size: '' };
            }
            return { color, size };
        }
    }

    return null;
}

/**
 * Resolve pure color/size for MDS UI from saved options.
 * Prefer SIZE when present; otherwise parse legacy COLOR-only via catalog colors.
 *
 * @param {{ color?: string, COLOR?: string, size?: string, SIZE?: string }} options
 * @param {Array} variants
 * @param {{ productId?: string|number, selectableFeatures?: string[] }} [ctx]
 * @returns {{ color: string, size: string, parsedFromLegacy: boolean, matchMode?: string }}
 */
export function resolveMdsColorSizeFromSavedOptions(options = {}, variants = [], ctx = {}) {
    const colorRaw = String(options.color || options.COLOR || '').trim();
    const sizeRaw = String(options.size || options.SIZE || '').trim();
    const resolved = resolveLegacySavedOptionForMds({
        combinedColor: colorRaw,
        savedSize: sizeRaw,
        variants,
        selectableFeatures: ctx.selectableFeatures || [],
        productId: ctx.productId
    });
    return {
        color: resolved.color,
        size: resolved.size,
        parsedFromLegacy: !!(resolved.matchMode && String(resolved.matchMode).includes('parsed')),
        matchMode: resolved.matchMode
    };
}

/**
 * @param {string[]|null|undefined} features
 * @returns {{ hasColor: boolean, hasSize: boolean, hasNA: boolean, raw: string[] }}
 */
export function normalizeSelectableFeatures(features) {
    const raw = Array.isArray(features) ? features.filter(Boolean) : [];
    const upper = raw.map((f) => String(f).toUpperCase());
    return {
        hasColor: upper.includes('COLOR'),
        hasSize: upper.includes('SIZE'),
        hasNA: upper.length === 1 && upper[0] === 'N/A',
        raw
    };
}

/**
 * Feature-aware resolve of legacy COLOR (+ optional SIZE) for MDS ON load.
 * - COLOR+SIZE: Steve color-suffix parse when SIZE key missing
 * - SIZE-only: never bind phantom colorName to UI; extract size from concat or exact size match
 * - COLOR-only: bind color; ignore size remainder for UI
 * - N/A: empty options
 *
 * @returns {{ color: string, size: string, matchMode: string }}
 */
export function resolveLegacySavedOptionForMds({
    combinedColor = '',
    savedSize = '',
    variants = [],
    selectableFeatures = [],
    productId = null
} = {}) {
    const features = normalizeSelectableFeatures(selectableFeatures);
    const colorRaw = String(combinedColor || '').trim();
    const sizeRaw = String(savedSize || '').trim();

    if (features.hasNA || (!features.hasColor && !features.hasSize)) {
        return { color: '', size: '', matchMode: 'na' };
    }

    // MDS-saved SIZE key present
    if (sizeRaw) {
        let color = colorRaw;
        if (features.hasColor && colorRaw && sizeRaw) {
            // COLOR may still be "Size Color" — strip known size
            const colorLower = colorRaw.toLowerCase();
            const sizeLower = sizeRaw.toLowerCase();
            if (colorLower.startsWith(`${sizeLower} `)) {
                color = colorRaw.slice(sizeRaw.length).trim();
            } else if (colorLower.endsWith(` ${sizeLower}`)) {
                color = colorRaw.slice(0, -(sizeRaw.length + 1)).trim();
            }
        }
        return {
            color: features.hasColor ? color : '',
            size: features.hasSize ? sizeRaw : '',
            matchMode: 'explicit-size'
        };
    }

    if (!colorRaw) {
        return { color: '', size: '', matchMode: 'empty' };
    }

    const sortedColors = (features.hasColor || features.hasSize)
        ? getSortedLegacyColorMatchList(productId, variants)
        : [];

    // SIZE-only: COLOR field is size or legacy "Size Color"
    if (features.hasSize && !features.hasColor) {
        const parsed = sortedColors.length
            ? parseLegacyConcatenatedColor(colorRaw, sortedColors, { preSorted: true })
            : null;
        if (parsed?.size) {
            return { color: '', size: parsed.size, matchMode: 'size-only-parsed' };
        }

        const lower = colorRaw.toLowerCase();
        for (let i = 0; i < (variants?.length || 0); i++) {
            const v = variants[i];
            const sn = (v.sizeName || v.SizeName || '').toLowerCase();
            const ag = (v.AgilitySize || v.agilitySize || '').toLowerCase();
            if (sn === lower || ag === lower) {
                return {
                    color: '',
                    size: v.sizeName || v.SizeName || colorRaw,
                    matchMode: 'size-only-exact'
                };
            }
            // Reverse combined Size+Color equals saved COLOR
            const vColor = v.colorName || v.ColorName || '';
            const vSize = v.sizeName || v.SizeName || '';
            const combined = vSize ? `${vSize} ${vColor}`.trim() : vColor;
            if (combined && combined.toLowerCase() === lower) {
                return { color: '', size: vSize || colorRaw, matchMode: 'size-only-combined' };
            }
        }

        // Last resort: use raw string as size candidate (caller may still fail match)
        return { color: '', size: colorRaw, matchMode: 'size-only-raw' };
    }

    // COLOR-only: COLOR field is color (or odd "Size Color" — take color, drop size for UI)
    if (features.hasColor && !features.hasSize) {
        const lower = colorRaw.toLowerCase();
        for (let i = 0; i < (variants?.length || 0); i++) {
            const v = variants[i];
            const cn = (v.colorName || v.ColorName || '').toLowerCase();
            if (cn === lower) {
                return {
                    color: v.colorName || v.ColorName,
                    size: '',
                    matchMode: 'color-only-exact'
                };
            }
            const vSize = v.sizeName || v.SizeName || '';
            const vColor = v.colorName || v.ColorName || '';
            const combined = vSize ? `${vSize} ${vColor}`.trim() : vColor;
            if (combined && combined.toLowerCase() === lower) {
                return { color: vColor, size: '', matchMode: 'color-only-combined' };
            }
        }

        const parsed = sortedColors.length
            ? parseLegacyConcatenatedColor(colorRaw, sortedColors, { preSorted: true })
            : null;
        if (parsed?.color) {
            return { color: parsed.color, size: '', matchMode: 'color-only-parsed' };
        }

        return { color: colorRaw, size: '', matchMode: 'color-only-raw' };
    }

    // COLOR + SIZE: Steve parser when SIZE key missing
    if (features.hasColor && features.hasSize) {
        const parsed = sortedColors.length
            ? parseLegacyConcatenatedColor(colorRaw, sortedColors, { preSorted: true })
            : null;
        if (parsed?.color) {
            return {
                color: parsed.color,
                size: parsed.size || '',
                matchMode: 'both-parsed'
            };
        }

        // Reverse combined match hints
        const lower = colorRaw.toLowerCase();
        for (let i = 0; i < (variants?.length || 0); i++) {
            const v = variants[i];
            const vColor = v.colorName || v.ColorName || '';
            const vSize = v.sizeName || v.SizeName || '';
            const combined = vSize ? `${vSize} ${vColor}`.trim() : vColor;
            if (combined && combined.toLowerCase() === lower) {
                return { color: vColor, size: vSize, matchMode: 'both-combined' };
            }
            if (vColor.toLowerCase() === lower) {
                return { color: vColor, size: '', matchMode: 'both-color-exact' };
            }
            if (vSize && vSize.toLowerCase() === lower) {
                return { color: '', size: vSize, matchMode: 'both-size-exact' };
            }
        }

        return { color: colorRaw, size: '', matchMode: 'both-raw' };
    }

    return { color: colorRaw, size: sizeRaw, matchMode: 'fallback' };
}

/**
 * Flag OFF load only — resolve a catalog variant from MDS-saved SelectedOptions.
 * Catalog uses flattened legacy ColorName (Size + " " + Color). MDS may have saved pure
 * color, combined COLOR + SIZE key, or size-only on COLOR. Prefers branch-available SKU.
 *
 * @param {Array} variants
 * @param {{ COLOR?: string, SIZE?: string, color?: string, size?: string }} selectedOptions
 * @returns {object|null}
 */
export function matchLegacyVariantFromMdsSavedOptions(variants = [], selectedOptions = {}) {
    const savedColor = String(selectedOptions.COLOR ?? selectedOptions.color ?? '').trim();
    const savedSize = String(selectedOptions.SIZE ?? selectedOptions.size ?? '').trim();
    if (!savedColor && !savedSize) {
        return null;
    }

    const sc = savedColor.toLowerCase();
    const ss = savedSize.toLowerCase();

    const variantMatchesSaved = (v) => {
        const vColor = String(v.colorName || v.ColorName || '').trim();
        const vSize = String(v.sizeName || v.SizeName || '').trim();
        const vAgility = String(v.AgilitySize || v.agilitySize || '').trim();
        const vColorLower = vColor.toLowerCase();
        const vSizeLower = vSize.toLowerCase();
        const vAgilityLower = vAgility.toLowerCase();

        const sizeMatches = !ss || vSizeLower === ss || vAgilityLower === ss;

        if (savedColor) {
            if (vColorLower === sc && sizeMatches) {
                return true;
            }
            const vCombined = vSize ? `${vSize} ${vColor}`.trim() : vColor;
            if (vCombined.toLowerCase() === sc && sizeMatches) {
                return true;
            }
            if (vSizeLower === sc || vAgilityLower === sc) {
                return true;
            }
            // MDS pure color on flattened catalog: "Ironwood" vs "Size Ironwood"
            if (!savedSize && !savedColor.includes(' ') && vColorLower.endsWith(` ${sc}`)) {
                return true;
            }
        } else if (ss) {
            return vSizeLower === ss || vAgilityLower === ss;
        }

        return false;
    };

    const matches = variants.filter(variantMatchesSaved);
    if (!matches.length) {
        return null;
    }

    const available = matches.filter(
        (v) => v.availableAtBranch || v.AvailableAtBranch || v.isAvailableAtBranch
    );
    return available[0] || matches[0];
}

/**
 * Flag OFF UI bind — index of matched variant in the given list (-1 if none).
 */
export function findLegacyVariantIndexFromSavedOptions(variants = [], selectedOptions = {}) {
    const match = matchLegacyVariantFromMdsSavedOptions(variants, selectedOptions);
    if (!match) {
        return -1;
    }
    const idx = variants.indexOf(match);
    if (idx >= 0) {
        return idx;
    }
    const code = match.itemCode || match.ItemCode;
    if (!code) {
        return -1;
    }
    return variants.findIndex(
        (v) => (v.itemCode || v.ItemCode) === code
    );
}

export function findVariantByItemCode(variants = [], itemCode = '') {
    const normalizedItemCode = String(itemCode || '').trim().toUpperCase();
    if (!normalizedItemCode) return null;
    return variants.find(variant =>
        String(variant?.itemCode || variant?.ItemCode || '').trim().toUpperCase() === normalizedItemCode
    ) || null;
}

/**
 * Find variant from resolved MDS color/size (case-insensitive; AgilitySize accepted for size).
 */
export function findVariantByColorSize(variants = [], { color = '', size = '' } = {}, checkAvailability = false) {
    if (!variants?.length) return null;
    const colorLower = String(color || '').trim().toLowerCase();
    const sizeLower = String(size || '').trim().toLowerCase();
    if (!colorLower && !sizeLower) return null;

    return variants.find((v) => {
        const vColor = (v.colorName || v.ColorName || '').toLowerCase();
        const vSize = (v.sizeName || v.SizeName || '').toLowerCase();
        const vAgility = (v.AgilitySize || v.agilitySize || '').toLowerCase();

        const colorOk = !colorLower || vColor === colorLower;
        const sizeOk = !sizeLower
            || vSize === sizeLower
            || vAgility === sizeLower;

        if (!colorOk || !sizeOk) return false;

        if (checkAvailability) {
            return !!(v.availableAtBranch || v.AvailableAtBranch || v.isAvailableAtBranch);
        }
        return true;
    }) || null;
}
